/**
 * Search-param validator for /portal/myapp/$tableId/submit.
 *
 * `edit` is typed as a number so TanStack Router's default search
 * serializer renders it as ?edit=7 rather than ?edit=%227%22 (string
 * values get JSON-quoted by the default serializer). Incoming string
 * values are still coerced for back-compat with old bookmarks.
 *
 * `mode` is narrowed to the literal "edit" because that is the only
 * value the submit page acts on (anything else falls into view mode).
 * A typo like ?mode=edt would silently drop the user into view mode
 * with the broader string type — narrowing prevents that class of bug.
 *
 * `from` is a navigation-source hint (e.g. "kanban" — set by KanbanView
 * so a future "Back to Kanban" affordance has the context). Validated
 * as a non-empty string so a non-string URL value (e.g. a number TanStack
 * decoded from a numeric-looking param) doesn't slip past the type
 * system and break a downstream string comparison.
 */
export type SubmitSearch = {
  edit?: number;
  cloneFrom?: number;
  from?: string;
  mode?: "edit";
};

function normaliseString(value: unknown): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}

function coerceRecordId(raw: unknown): number | undefined {
  // Same coercion contract as `edit`: numeric id, with back-compat for
  // string-encoded URL params. Returns undefined for blank / NaN /
  // non-numeric. Zero is preserved (it is a valid PK in virtual tables).
  if (typeof raw === "number") {
    return isNaN(raw) ? undefined : raw;
  }
  if (typeof raw === "string") {
    if (raw.trim() === "") return undefined;
    const n = Number(raw);
    return isNaN(n) ? undefined : n;
  }
  return undefined;
}

export function validateSubmitSearch(search: Record<string, unknown>): SubmitSearch {
  const from = normaliseString(search.from);
  const mode = search.mode === "edit" ? "edit" : undefined;
  return {
    edit: coerceRecordId(search.edit),
    cloneFrom: coerceRecordId(search["clone-from"] ?? search.cloneFrom),
    from,
    mode,
  };
}
